import { useEffect, type RefObject } from "react"

interface UseFocusFollowsActiveRowOptions {
  activeItemId: string | null
  containerRef: RefObject<HTMLElement | null>
  selectorAttribute: string
}

/**
 * While a table row inside the container has focus, moves focus to the row
 * the keyboard highlight (j/k/arrows) lands on, so the focus ring and the
 * highlight name the same row and Enter opens it. Focus elsewhere is left alone.
 */
export function useFocusFollowsActiveRow({
  activeItemId,
  containerRef,
  selectorAttribute,
}: UseFocusFollowsActiveRowOptions) {
  useEffect(() => {
    const container = containerRef.current
    const focused = document.activeElement
    if (
      !activeItemId ||
      !container ||
      !(focused instanceof HTMLTableRowElement) ||
      !container.contains(focused)
    ) {
      return
    }
    const target = container.querySelector<HTMLElement>(
      `[${selectorAttribute}="${CSS.escape(activeItemId)}"]`,
    )
    if (target && target !== focused) {
      target.focus({ preventScroll: true })
    }
  }, [activeItemId, containerRef, selectorAttribute])
}
